import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { API_URL_COMPANIES } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';

import { Publication, PublicationPayload } from '../shared/models';

@Injectable({
  providedIn: 'root',
})
export class PublicationsService {
  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private asyncRequestService: AsyncRequestService,
    private errorHandlingService: ErrorHandlingService,
    private http: HttpClient,
    private sitesServices: SitesService
  ) {}

  private getBaseSiteUrl(siteId: number): string {
    return `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/publications2`;
  }

  getSitesToPublish() {
    return this.sitesServices.sitesV2.filter(
      site =>
        // facebook or twitter
        (site.type === 1 || site.type === 2) &&
        this.alcmeonContextService.hasCapability(site.id, CapabilityIdEnum.Publish)
    );
  }

  createPublication(siteId: number, parentPublicationId: number): Observable<{ publication_id: number }> {
    const url = `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/publications2`;

    const data = {
      site_id: siteId,
      parent_publication_id: parentPublicationId,
    };

    return this.http
      .post<{ publication_id: number }>(url, data)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  updatePublication(
    siteId: number,
    publicationId: number,
    data: PublicationPayload
  ): Observable<{ publication: Publication }> {
    return this.http
      .put<{ publication: Publication }>(`${this.getBaseSiteUrl(siteId)}/${publicationId}`, data)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  publishNow(siteId: number, publicationId: number): Observable<any> {
    return this.asyncRequestService
      .post(`${this.getBaseSiteUrl(siteId)}/${publicationId}/publish-now`, {})
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * No pipe catchAlertAndThrowHttpError, catched in component.
   */
  schedule(siteId: number, publicationId: number): Observable<any> {
    return this.asyncRequestService.post(`${this.getBaseSiteUrl(siteId)}/${publicationId}/schedule`, {});
  }

  markAsDraft(siteId: number, publicationId: number): Observable<{ publication: Publication }> {
    return this.http
      .post<{ publication: Publication }>(`${this.getBaseSiteUrl(siteId)}/${publicationId}/mark-as-draft`, null)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  checkMediaUpload(siteId: number, mediaId: number, mediaType: string): Observable<string> {
    const url = `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/medias/${mediaId}/check-upload`;

    return this.asyncRequestService
      .post<string>(url, {
        media_type: mediaType,
      })
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }
}
